import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface SortableListProps<T> {
  items: T[];
  getId: (item: T) => string;
  /** Called with the full id list in its new order after a drop. */
  onReorder: (orderedIds: string[]) => void;
  /** Read-only mode: rows render without drag handles. */
  disabled?: boolean;
  className?: string;
  /** Render one row. Place `handle` wherever the grip should sit (null when disabled). */
  renderItem: (item: T, handle: ReactNode) => ReactNode;
}

/** Pure reorder computation for a drag from `activeId` onto `overId`.
 *  Returns the new id order, or null when the drop is a no-op. */
export function computeReorder(
  ids: string[],
  activeId: string,
  overId: string,
): string[] | null {
  if (activeId === overId) return null;
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from < 0 || to < 0) return null;
  return arrayMove(ids, from, to);
}

/** Vertical drag-to-reorder list (dnd-kit). Rows are direct children of the
 *  container so parent `divide-y` / `gap` styling keeps working. Keyboard
 *  accessible: focus a handle, Space to lift, arrows to move, Space to drop. */
export function SortableList<T>({
  items,
  getId,
  onReorder,
  disabled,
  className,
  renderItem,
}: SortableListProps<T>) {
  const sensors = useSensors(
    // A small activation distance keeps plain clicks (checkboxes, inputs)
    // from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    const next = computeReorder(ids, String(active.id), String(over.id));
    if (next) onReorder(next);
  }

  if (disabled) {
    return (
      <div className={className}>
        {items.map((item) => (
          <div key={getId(item)}>{renderItem(item, null)}</div>
        ))}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item) => (
            <SortableRow key={getId(item)} id={getId(item)}>
              {(handle) => renderItem(item, handle)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  children,
}: {
  id: string;
  children: (handle: ReactNode) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      className="cursor-grab touch-none rounded p-0.5 text-muted-foreground/50 hover:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring active:cursor-grabbing"
      aria-label="Drag to reorder"
    >
      <GripVertical className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && "relative z-10 bg-background opacity-80 shadow-md")}
    >
      {children(handle)}
    </div>
  );
}
